import { useEffect, useMemo, useState } from 'react'

import { useFloorPlan } from '../hooks/useFloorPlan'
import { useSelector } from 'react-redux'

import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { Box, Flex, IconButton } from '@chakra-ui/react'

import {
  selectEditMode,
  selectFacilityDrawerOpen,
  selectSideNavOpen,
} from '@/redux/ui/uiSlice'
import { FacilityDrawer } from '@/redux/ui/uiSlice.types'
import { mixpanel } from '@/utils/analytics'

import { CheckPointsPanel } from '../checkpoints-panel'
import { DevicesPanel } from '../devices-panel'
import {
  DevicePopoverContent,
  FloorPlanPopover,
  TailgatePopoverContent,
} from '../floor-plan-popover'
import { FloorsPanel } from '../floors-panel'
import { useImageSize } from '../hooks'
import { Container } from '../styles/StyledContainer'
import { TailgateConnectionModals } from '../tailgate-connection'
import { TourPlansPanel } from '../tour-plans-panel'
import { FloorI, TourPlanI } from '../types/types'
import { mapCheckpointsData } from '../utils/mappedFacilityData'
import { FloorChartContainer } from './FloorChartContainer'
import { FloorPlanPlaceholder } from './FloorPlanPlaceholder'
import { RemoveCheckPointModal } from './RemoveCheckPointModal'

const getDrawerWidth = (drawer: FacilityDrawer) => {
  // TODO: have to come up with a better way of calculating
  // the width of the "drawer" component.
  switch (drawer) {
    case 'devices':
      return 390
    case 'checkpoints':
      return 420
    case 'tour-plans':
      return 390
    case 'floors':
      return 390
  }
}

// Collapsed panel width (just enough for the toggle button)
const COLLAPSED_WIDTH = 40

interface FloorPlanIProps {
  size: {
    width: number
    height: number
  }
  facilityId: string
  currentFloorId: string
  tourPlans: TourPlanI[]
  floors: FloorI[]
  onReorderFloors?: (floorIds: string[]) => void
}

export const FloorPlan = ({
  size,
  facilityId,
  currentFloorId,
  tourPlans,
  floors,
  onReorderFloors,
}: FloorPlanIProps) => {
  const { width, height } = size
  const isSideNavOpen = useSelector(selectSideNavOpen)
  const drawerOpen = useSelector(selectFacilityDrawerOpen)
  const isEditMode = useSelector(selectEditMode)
  const isGuardTour =
    drawerOpen === 'checkpoints' || drawerOpen === 'tour-plans'

  const [isPanelExpanded, setIsPanelExpanded] = useState(true)

  const {
    checkpointDevices,
    deviceHoverItem,
    devices,
    imageUrl,
    isDeviceHovering,
    isRemoveCheckPointOpen,
    isTailgateHovering,
    isFloorDataLoading,
    connections,
    isConnectionsLoading,
    connectDevices,
    disconnectDevices,
    getConnection,
    onDeviceClicked,
    onRemoveCheckPointClose,
    onTileDragStart,
    selectedDevice,
    setHighlightedDeviceIds,
    tailgateHoverItem,
  } = useFloorPlan()
  const { imageSize } = useImageSize(imageUrl)

  const onPanelItemHoverIn = (deviceId: string) => {
    setHighlightedDeviceIds([deviceId])
  }

  const onPanelItemsHoverIn = (deviceId: string[]) => {
    setHighlightedDeviceIds(deviceId)
  }

  const onPanelItemHoverOut = () => {
    setHighlightedDeviceIds(null)
  }

  const togglePanelExpansion = () => {
    setIsPanelExpanded(!isPanelExpanded)
  }

  const fullRightPanelWidth = getDrawerWidth(drawerOpen)

  const rightPanelWidth = isPanelExpanded
    ? fullRightPanelWidth
    : COLLAPSED_WIDTH

  const chartInsets = {
    left: isSideNavOpen ? 240 : 60,
    top: 65,
    right: rightPanelWidth,
    bottom: 0,
  }
  const chartSize = [
    width - (chartInsets.left + chartInsets.right),
    height - chartInsets.top,
  ]

  useEffect(() => {
    const numberOfDevices = devices.length
    isFloorDataLoading
      ? mixpanel.time_event('Page Load: Floor Plan View')
      : mixpanel.track('Page Load: Floor Plan View', {
          number_of_devices: numberOfDevices,
        })
  }, [isFloorDataLoading])

  const mappedCheckpoints = useMemo(
    () => mapCheckpointsData(checkpointDevices),
    [devices]
  )

  return (
    <Box h='100%'>
      <Container>
        <Flex alignItems='stretch' h='100%'>
          <Flex flexGrow={1} justifyContent='center' pos='static'>
            <FloorPlanPlaceholder
              fetching={isFloorDataLoading}
              imageSize={imageSize}
              imageUrl={imageUrl}
            />
            {imageSize && (
              <FloorChartContainer
                chartInsets={chartInsets}
                chartSize={chartSize}
                connections={connections}
                devices={devices}
                imageSize={imageSize}
                imageUrl={imageUrl}
                isGuardTour={isGuardTour}
                showBinIcon={isEditMode && !isGuardTour}
              />
            )}
          </Flex>
          <Flex position='relative' w={`${rightPanelWidth}px`}>
            <IconButton
              aria-label={isPanelExpanded ? 'Collapse panel' : 'Expand panel'}
              backgroundColor='white'
              border='1px solid'
              borderColor='gray.200'
              borderRadius='full'
              boxShadow='sm'
              data-testId='FloorPlan:ExpandPanelToggle'
              icon={
                isPanelExpanded ? (
                  <ChevronRightIcon boxSize='25px' />
                ) : (
                  <ChevronLeftIcon boxSize='25px' />
                )
              }
              onClick={togglePanelExpansion}
              position='absolute'
              size='sm'
              top={45}
              transform='translateY(-50%) translateX(-50%)'
              zIndex='1'
            />

            {isPanelExpanded && (
              <Box overflow='hidden' width='100%'>
                {drawerOpen === 'devices' && (
                  <DevicesPanel
                    devices={devices}
                    onDeviceClicked={onDeviceClicked}
                    onDeviceItemHoverIn={onPanelItemHoverIn}
                    onDeviceItemHoverOut={onPanelItemHoverOut}
                    onTileDragStart={onTileDragStart}
                  />
                )}
                {drawerOpen === 'floors' && (
                  <FloorsPanel
                    currentFloorId={currentFloorId}
                    floors={floors}
                    onReorderFloors={onReorderFloors}
                  />
                )}
                {drawerOpen === 'checkpoints' && (
                  <CheckPointsPanel
                    checkpoints={mappedCheckpoints}
                    facilityId={facilityId}
                    floorId={currentFloorId}
                    onDeviceItemHoverIn={onPanelItemHoverIn}
                    onDeviceItemHoverOut={onPanelItemHoverOut}
                    onTileDragStart={onTileDragStart}
                  />
                )}
                {drawerOpen === 'tour-plans' && (
                  <TourPlansPanel
                    currentFloorId={currentFloorId}
                    facilityId={facilityId}
                    onDeviceItemHoverIn={onPanelItemHoverIn}
                    onDeviceItemHoverOut={onPanelItemHoverOut}
                    onDeviceItemsHoverIn={onPanelItemsHoverIn}
                    tourPlans={tourPlans}
                  />
                )}
              </Box>
            )}
          </Flex>
        </Flex>
      </Container>
      <FloorPlanPopover
        elementId={tailgateHoverItem?.elementId}
        isHovering={isTailgateHovering}
        kind='tailgate'
      >
        <TailgatePopoverContent
          cameraName={tailgateHoverItem?.cameraName || ''}
          connection={tailgateHoverItem?.connection}
          deviceName={tailgateHoverItem?.doorName || ''}
        />
      </FloorPlanPopover>
      <FloorPlanPopover
        elementId={deviceHoverItem?.elementId}
        isHovering={isDeviceHovering}
        kind='device'
      >
        <DevicePopoverContent
          deviceId={deviceHoverItem?.deviceId}
          deviceName={deviceHoverItem?.deviceName}
          deviceType={deviceHoverItem?.deviceType}
        />
      </FloorPlanPopover>
      <RemoveCheckPointModal
        checkpoint={selectedDevice}
        facilityId={facilityId}
        floorId={currentFloorId}
        isOpen={isRemoveCheckPointOpen}
        onClose={onRemoveCheckPointClose}
        tourPlans={tourPlans}
      />
      <TailgateConnectionModals
        connectDevices={connectDevices}
        connections={connections}
        disconnectDevices={disconnectDevices}
        getConnection={getConnection}
        isConnectionsLoading={isConnectionsLoading}
      />
    </Box>
  )
}
